import '../field/ui-field.js';
import '../curtains/ui-curtain.js';
import '../curtains/ui-curtains.js';
import { browser } from '../../global/browser.js';
import { UIElement } from '../ui-element.js';
import { deepFreeze, getLanguageForCountry } from '../../global/helpers.js';
import {
    createElement,
    TimeInSeconds,
    updateElement,
} from '../../global/ui-helpers.js';
import { TabIndex } from '../../global/keyboard.js';
import { UIModal } from '../modal/ui-modal.js';
import { Digest } from '../../global/digest.js';
import {
    type CookieConsentCategories,
    CookieConsentCategory,
    type CookieConsentCategoryKey,
    type CookieConsentValue,
} from './ui-cookie-consent.types.js';
import { Attribute } from '../../decorators/attribute.decorator.js';
import { Child } from '../../decorators/child.decorator.js';
import { Component } from '../../decorators/component.decorator.js';
import type { UICurtain } from '../curtains/ui-curtain.js';
import { UIDropdown } from '../dropdown/ui-dropdown.js';
import type { Country, Language } from '../../global/global.type.js';
import type { IElementConfig, IPropsChildren } from '../ui-element.types.js';
import { Labels } from '../../global/labels.js';
import type { StorageDriver } from '../../types/storage.type.js';
import { TranslationLabels } from '../../types/labels.type.js';
import styles from './ui-cookie-consent.css';

interface CookieConsentLanguageChangeEvent extends CustomEvent {
    target: HTMLSelectElement | UIDropdown;
}

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UICookieConsent
 * @element ui-cookie-consent
 * @classdesc Represents a class for <code>ui-cookie-consent</code> element.
 * @fires event:cookie-consent-changed
 * @property {number} [expiresDays=365] {@attr expires-days} Cookies consent expiration in days.
 * @property {boolean} [forceShow] {@attr force-show} Force display cookie consent.
 * @property {boolean} [forceHide] {@attr force-hide} Forces to hide cookie consent.
 * consent UI, doesn't respect if consent is given or not.
 * @property {string} hrefPolicy {@attr href-policy} Link to page about cookie, where
 * are details described.
 * @property {string} hrefEntities {@attr href-entities} Link to page about Swedbank entities.
 * @property {string} hrefCookies {@attr href-cookies} Link to page about cookies.
 * @property {string} hrefSettings {@attr href-settings} Link to page change settings.
 * @property {string} hrefUsage {@attr href-usage} Link to cookies usage page.
 * @property {string} target {@attr target} Specifies were 'about-link' will be opened,
 * values are similar like for &lt;a> attribute.
 * @property {Country} [country="EE"] {@attr country} Sets the country
 * where consent is shown.
 * @property {Language} language {@attr language} Sets the language
 * of the cookie consent element.
 * @property {boolean} handleStorage {@attr handle-storage} Also deletes localStorage data
 * @property {string} consentVersion {@attr consent-version} Version of the current consent.
 * Verifies checks if an user has same consent version as request in component, nothing to do.
 * Otherwise re-ask the user consent again.
 * @property {string} prefix Prefix of the storage key
 * @property {CookieConsentCategories} whitelist Whitelist of the cookies and storage, set from the
 * external service. Only very basic with one COOKIE_CONSENT is provided by default.
 * @property {UIModal} container {@readonly} Shortcut to container div.
 * @property {UICurtain} settings {@readonly} Shortcut to settings curtain.
 * @property {HTMLAnchorElement} declineButton {@readonly} Shortcut to decline button.
 * @property {HTMLButtonElement} acceptButton {@readonly} Shortcut to accept all button.
 * @property {HTMLButtonElement} saveChoiceButton {@readonly} Shortcut to accept settings button.
 * @property {HTMLAnchorElement} entitiesLink {@readonly} Shortcut to change link in info text.
 * @property {UIDropdown} langSelector {@readonly} Shortcut to change language selector.
 * @property {Function} warn Warning function for disallowed cookies is set from outside, can be set
 * outside.
 * @example
 * <ui-cookie-consent></ui-cookie-consent>
 */
@Component({
    tag: 'ui-cookie-consent',
    styles,
})
export class UICookieConsent extends UIElement {
    public _languageSwitcher!: (lang: Language) => void;

    public static ConsentName = 'COOKIE_CONSENT';
    public static Category: Record<
        CookieConsentCategoryKey,
        CookieConsentCategory
    > = {
        Necessary: CookieConsentCategory.Necessary,
        Analytical: CookieConsentCategory.Analytical,
        Targeting: CookieConsentCategory.Targeting,
        Functional: CookieConsentCategory.Functional,
    };

    private _prefix!: string;
    private windowLoadHandler!: () => void;
    private storageDrivers = new Set<StorageDriver>();

    private static _whitelist: CookieConsentCategories;
    private static _warn: (...args: string[]) => void;
    private static AllowedCategory = 1;
    private static DisallowedCategory = 0;

    @Attribute() expiresDays = 365;
    @Attribute() forceShow = false;
    @Attribute() forceHide = false;
    @Attribute(String) hrefPolicy!: string;
    @Attribute(String) hrefSettings!: string;
    @Attribute(String) hrefEntities!: string;
    @Attribute(String) hrefCookies!: string;
    @Attribute(String) hrefUsage!: string;
    @Attribute(String) target!: string;
    @Attribute(String) country: Country = 'EE';
    @Attribute(String) language!: string;
    @Attribute() handleStorage = false;
    @Attribute() consentVersion: string = '';

    @Child('.ui-cookie-consent__container') container!: UIModal;
    @Child('.ui-cookie-consent__settings') settings!: UICurtain;
    @Child('.ui-cookie-consent__decline-button')
    declineButton!: HTMLButtonElement;
    @Child('.ui-cookie-consent__accept-button')
    acceptButton!: HTMLButtonElement;
    @Child('.ui-cookie-consent__save-choice-button')
    saveChoiceButton!: HTMLButtonElement;
    @Child('.ui-cookie-consent__lang') langSelector!: UIDropdown;

    constructor() {
        super();
        // Add native localStorage. In case more things are required in the future
        // such as cache, sessionStorage, RAM, etc. Can be added with similar way.
        // Just implementing interface.
        this.addStorageDriver({
            key: (i) => window.localStorage.key.call(window.localStorage, i),
            length: () => window.localStorage.length,
            set: window.localStorage.setItem.bind(window.localStorage),
            get: window.localStorage.getItem.bind(window.localStorage),
            remove: window.localStorage.removeItem.bind(window.localStorage),
            clear: window.localStorage.clear.bind(window.localStorage),
        });
    }

    /**
     * Prefix for storage, external client might use prefixes.
     * @type {string}
     */
    get prefix() {
        return this._prefix || '';
    }
    set prefix(prefix: string) {
        this._prefix = prefix;
    }

    /**
     * @type {Function}
     */
    get languageSwitcher() {
        return this._languageSwitcher;
    }
    set languageSwitcher(fn: (lang: Language) => void) {
        this._languageSwitcher = fn;
    }

    /**
     * Warning function for disallowed cookies is set from outside.
     * @type {Function | undefined}
     */
    static get warn() {
        return UICookieConsent._warn;
    }
    static set warn(fn: (...args: string[]) => void) {
        UICookieConsent._warn = fn;
    }

    /**
     * Adds a storage service to the consent.
     * @param {StorageDriver} driver
     */
    addStorageDriver(driver: StorageDriver): void {
        this.storageDrivers.add(driver);
    }

    /**
     * Gets all storage drivers for the consent component.
     * @returns {Set<StorageDriver>}
     */
    getStorageDrivers(): Set<StorageDriver> {
        return this.storageDrivers;
    }

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-cookie-consent', {
            title: 'We use cookies',
            info: 'Swedbank Group {entitiesLink} registered in {country} use {cookiesLink} (or similar technologies) to provide you personalized information about our products and services and to improve performance of the website. If you give your consent to all these cookies, please press “Accept”. If you want to manage your preferences, please press “Select your own cookies”, or press “Decline” to decline all these cookies. Your choice for cookies will be stored for one year.',
            disclaimer:
                'Some cookies are necessary for this website functioning and processed based on our legitimate interest, therefore your consent is not asked.',
            accept: 'Accept',
            decline: 'Decline',
            saveChoice: 'Save my choice',
            myCookies: 'Select your own cookies',
            policyLinkLabel: 'Cookie Policy',
            HELP_NECESSARY:
                'Are necessary for the website to function. These cookies cannot be switched off; hence the user consent is not asked.',
            HELP_ANALYTICAL:
                'Help to improve performance of the website by counting site visits and traffic sources.  If declined, your browsing of this website will not be part of our statistics.',
            HELP_TARGETING:
                'Help to provide you with personalized information about our products and services. If declined, personalized content will not be presented to you.',
            HELP_FUNCTIONAL:
                'These cookies enable enhanced website functionalities. If you decline these cookies it may limit some functionality but core website operations remain unaffected.',
            CATEGORY_NECESSARY: 'Necessary',
            CATEGORY_ANALYTICAL: 'Analytical',
            CATEGORY_TARGETING: 'Targeting',
            CATEGORY_FUNCTIONAL: 'Functional',
            EE: 'Estonia',
            LV: 'Latvia',
            LT: 'Lithuania',
            entities: 'entities',
            cookies: 'cookies',
            language: 'Language',
        });
    }

    /**
     * Whitelist of all Swedbank's approved cookies. This can be overridden or
     * expanded from other services.
     * @returns {Readonly<CookieConsentCategories>}
     */
    static get whitelist(): Readonly<CookieConsentCategories> {
        if (!this._whitelist) {
            this._whitelist = {
                NECESSARY: [this.ConsentName],
                ANALYTICAL: [],
                TARGETING: [],
                FUNCTIONAL: [],
            };
        }
        return deepFreeze(this._whitelist) as Readonly<CookieConsentCategories>;
    }
    static set whitelist(list: CookieConsentCategories) {
        this._whitelist = list;
    }

    /**
     * Deletes absolutely all cookies for the domain.
     */
    destroyAllCookies(): void {
        document.cookie.split(';').forEach(this.destroyCookieByName.bind(this));
    }

    /**
     * Withdraws with cookie consent, remove COOKIE_CONSENT and shows
     * cookie dialog again.
     */
    withdraw(): void {
        document.cookie = `${UICookieConsent.ConsentName}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
        this.show();
    }

    /**
     * Show view with info view (first layer).
     */
    showInfoView(): void {
        this.open();
    }

    /**
     * Show view with options, ignoring intro text (second layer).
     */
    showOptionsView(): void {
        this.settings.activate();
        this.open();
    }

    /**
     * Opens the cookie consent dialog.
     */
    open(): void {
        const containerRef = UICookieConsent.props.children
            ?.container as IPropsChildren;
        const selector = containerRef.selector || containerRef;
        this.dispatchCustomEvent('modal-open', {
            selector,
        });
        this.container.focus();
    }

    /**
     * Closes the cookie consent dialog.
     */
    close(): void {
        this.container.close();
    }

    /**
     * Destroys all unwanted cookies.
     */
    destroyUnwantedCookies(): void {
        const whitelistedCookies = this.getWhitelisted();
        // 1. Destroy every cookie which isn't whitelisted categories.
        // 2. Destroy every cookie which isn't in whitelist at all.
        // But usually set manually or by 3rd parties.
        this.getAllCookiesList().forEach((cookieName) => {
            let isAllowedCookie = false;
            for (let i = 0; i < whitelistedCookies.length; ++i) {
                if (cookieName.indexOf(whitelistedCookies[i]) === 0) {
                    isAllowedCookie = true;
                    break;
                }
            }
            if (!isAllowedCookie) {
                this.destroyCookieByName(cookieName);
            }
            if (
                cookieName !== '' &&
                !this.isCookieWhitelisted(cookieName) &&
                typeof UICookieConsent.warn === 'function'
            ) {
                UICookieConsent.warn(
                    'COOKIES WARNING: %c' +
                        cookieName +
                        '%c is not approved in' +
                        ' Swedbank group. It is automatically removed!',
                    'font-weight: bold;',
                    'font-weight: normal;'
                );
            }
        });
    }

    /**
     * Destroys all unwanted storage.
     */
    destroyUnwantedStorage(): void {
        if (!this.handleStorage) {
            return;
        }
        const whiteListedStorage = this.getWhitelisted();
        this.getStorageDrivers().forEach((driver) => {
            const storageList: string[] = [];
            for (let i = 0; i < driver.length(); ++i) {
                const keyValue = driver.key(i);
                if (keyValue !== null) {
                    storageList.push(keyValue);
                }
            }
            storageList.forEach((storageKey) => {
                let isStorageKeyAllowed = false;
                for (let i = 0; i < whiteListedStorage.length; ++i) {
                    if (
                        (this.prefix + storageKey).indexOf(
                            whiteListedStorage[i]
                        ) !== -1 ||
                        storageKey.indexOf(whiteListedStorage[i]) === 0
                    ) {
                        isStorageKeyAllowed = true;
                        break;
                    }
                }
                if (!isStorageKeyAllowed) {
                    driver.remove(storageKey);
                    if (typeof UICookieConsent.warn === 'function') {
                        UICookieConsent.warn(
                            'STORAGE WARNING: %c' +
                                storageKey +
                                '%c is not approved in' +
                                ' Swedbank group. It is automatically removed!',
                            'font-weight: bold;',
                            'font-weight: normal;'
                        );
                    }
                }
            });
        });
    }

    /**
     * Get whitelisted cookies or storage.
     * @returns {string[]}
     */
    getWhitelisted(): string[] {
        return this.getAllowedCategories().reduce<CookieConsentCategory[]>(
            (acc, category) => {
                const cookies = UICookieConsent.whitelist[category].filter(
                    (cookieName) => {
                        // Some cookies hava dynamic names and begins with prefix given in whitelist.
                        return cookieName.indexOf(cookieName) === 0;
                    }
                ) as CookieConsentCategory[];
                return acc.concat(cookies);
            },
            []
        );
    }

    /**
     * Get allowed categories by user.
     * @returns {CookieConsentCategory[]}
     */
    getAllowedCategories(): CookieConsentCategory[] {
        const selectedCategories = this.getSelectedCategories();
        const keys = Object.keys(selectedCategories) as CookieConsentCategory[];
        return keys.filter((category) => {
            return (
                UICookieConsent.AllowedCategory === selectedCategories[category]
            );
        }) as CookieConsentCategory[];
    }

    /**
     * Destroys cookies by its name.
     * @param {string} cookieName
     */
    destroyCookieByName(cookieName: string): void {
        const params = [
            cookieName + '=',
            'expires=Thu, 01 Jan 1970 00:00:00 GMT',
        ];
        if (this.isSecureContext()) {
            params.push('Secure=true');
            params.push('SameSite=Strict');
        }
        params.push('path=/');
        const entry = params.join(';');
        document.cookie = entry;
        [
            location.hostname,
            location.hostname.replace(/^([a-z0-9]+\.)?/, '.'),
        ].forEach((domain) => {
            document.cookie = entry + ';domain=' + domain;
        });
    }

    /**
     * Creates checkboxes for categories in 'settings' view.
     * @returns {HTMLUListElement}
     * @private
     */
    private buildCategoriesList(): HTMLUListElement {
        const consentValues = this.getSelectedCategories();
        return createElement(
            {
                tagName: 'ul',
                classList: {
                    'input-list': true,
                },
                children: Object.values<CookieConsentCategory>(
                    UICookieConsent.Category
                ).map((category) => {
                    return {
                        tagName: 'li',
                        classList: {
                            '-hidden':
                                category == UICookieConsent.Category.Functional,
                        },
                        children: [
                            {
                                tagName: 'input',
                                classList: {
                                    'ui-cookie-consent__category-checkbox':
                                        true,
                                },
                                attributes: {
                                    name: category,
                                    id: 'cookie-consent-category-' + category,
                                    type: 'checkbox',
                                    disabled: [
                                        UICookieConsent.Category.Necessary,
                                        UICookieConsent.Category.Functional,
                                    ].includes(category),
                                    checked:
                                        category ===
                                            UICookieConsent.Category
                                                .Necessary ||
                                        consentValues[category] ===
                                            UICookieConsent.AllowedCategory,
                                },
                            },
                            {
                                tagName: 'label',
                                attributes: {
                                    for: 'cookie-consent-category-' + category,
                                },
                                children: [
                                    UICookieConsent.labels[
                                        'CATEGORY_' + category
                                    ],
                                    createElement(
                                        this.createCategoryTooltip(category)
                                    ),
                                ],
                            },
                        ],
                    };
                }),
            },
            true
        );
    }

    /**
     * Gets all selected settings.
     * @returns {CookieConsentValue}
     */
    getSelectedCategories(): CookieConsentValue {
        let json: CookieConsentValue = {
            [UICookieConsent.Category.Necessary]: 0,
            [UICookieConsent.Category.Targeting]: 0,
            [UICookieConsent.Category.Analytical]: 0,
            [UICookieConsent.Category.Functional]: 0,
        } as unknown as CookieConsentValue;

        const consent = this.getEncodedConsentValue();
        try {
            json = JSON.parse(Digest.base64UnicodeDecode(consent));
        } catch (e) {
            if (!this.container?.isActive()) {
                console.warn(
                    'Cannot parse consent value from cookies. Withdrawing consent.'
                );
                this.withdraw();
            }
        }
        return json;
    }

    /**
     * Returns the current version of an user consent.
     * @returns {string}
     */
    getConsentVersion(): string {
        return this.getSelectedCategories().VERSION || '';
    }

    /**
     * Gets the consent value extracted from document.cookie.
     * e.g. name=ABCDEF;maxAge=1000;domain=...
     * Will extract ABCDEF.
     * Value decoded in base64.
     * @returns {string}
     */
    getConsentString(): string {
        return (
            document.cookie
                .split(';')
                .find((cookie) =>
                    cookie.includes(UICookieConsent.ConsentName)
                ) || ''
        );
    }

    /**
     * Create consent cookie in browser.
     * NB! There might be an issue for Chrome/Safari to set cookies on 'localhost'
     * without HTTPS enabled. If you have such issue please use IP address
     * 127.0.0.1 or any other local IP address instead of 'http://localhost/'.
     * @param {string} value
     */
    createCookieConsent(value: string): void {
        const cookieParts = [
            `${UICookieConsent.ConsentName}=${Digest.base64UnicodeEncode(value)}`,
            'path=/',
            'domain=' + browser.resolveMainDomain(),
            'max-age=' + this.expiresDays * TimeInSeconds.Day,
        ];
        if (this.isSecureContext()) {
            cookieParts.push('Secure=true');
            cookieParts.push('SameSite=Strict');
        } else {
            cookieParts.push('SameSite=Lax');
        }
        document.cookie = cookieParts.join('; ');
    }

    /**
     * Checks that context of the page is secure (https).
     * @returns {boolean}
     */
    private isSecureContext(): boolean {
        return window.location.protocol === 'https:';
    }

    /**
     * Creates about cookie link with label and link.
     * @returns {HTMLAnchorElement}
     * @private
     */
    private createPolicyLink(): HTMLAnchorElement {
        return createElement({
            tagName: 'a',
            attributes: {
                href: this.hrefPolicy || '#',
                target: this.target || null,
                rel: this.target === '_blank' ? 'noopener' : null,
            },
            classList: {
                'ui-cookie-consent__policy-link': true,
            },
            children: [UICookieConsent.labels.policyLinkLabel],
        });
    }

    /**
     * Creates change cookie link with label and link.
     * @returns {HTMLAnchorElement}
     * @private
     */
    private createEntitiesLink(): HTMLAnchorElement {
        return createElement({
            tagName: 'a',
            attributes: {
                href: this.hrefEntities || '#',
            },
            classList: {
                'ui-cookie-consent__entities-link': true,
            },
            children: [UICookieConsent.labels.entities],
        });
    }

    /**
     * Creates cookies usage link with label and link.
     * @returns {HTMLAnchorElement}
     * @private
     */
    private createUsageLink(): HTMLAnchorElement {
        return createElement({
            tagName: 'a',
            attributes: {
                href: this.hrefUsage || '#',
            },
            classList: {
                'ui-cookie-consent__usage-link': true,
            },
            children: [UICookieConsent.labels.usage],
        });
    }

    /**
     * Creates cookie link.
     * @returns {HTMLAnchorElement}
     * @private
     */
    private createCookieLink(): HTMLAnchorElement {
        return createElement({
            tagName: 'a',
            attributes: {
                href: this.hrefCookies || '#',
            },
            classList: {
                'ui-cookie-consent__cookie-link': true,
            },
            children: [UICookieConsent.labels.cookies],
        });
    }

    /**
     * Gets the correct country name based on attribute.
     * @returns {Label}
     * @private
     */
    private resolveCountryName(): TranslationLabels.Label {
        return UICookieConsent.labels[this.country];
    }

    /**
     * Check if cookies has consent given.
     * @returns {boolean}
     */
    isConsentGiven(): boolean {
        return (
            document.cookie.includes(UICookieConsent.ConsentName) &&
            this.isConsentValid()
        );
    }

    /**
     * Checks if cookie value of consent is a valid object.
     * @returns {boolean}
     */
    isConsentValid(): boolean {
        try {
            const jsonString = Digest.base64UnicodeDecode(
                this.getEncodedConsentValue()
            );
            JSON.parse(jsonString);
            // TODO GUI-3227 wait for green light for release
            // return this.getConsentVersion() === this.consentVersion;
            return true;
        } catch {
            return false;
        }
    }

    /**
     * NOTE base64 might contain '=' equal sign at the end.
     * So we are interested only in value with index 1 (2nd element).
     * possible values are:
     *  - name=BASE64ABCDEDF==
     *  - name=BASE64ABCDEDF=
     *  - name=BASE64ABCDEDF
     * We are always interested in 2nd part just after '=' BASE64ABCDEDF.
     * @returns {string}
     * @private
     */
    private getEncodedConsentValue(): string {
        return this.getConsentString().split('=')[1];
    }

    /**
     * Saves cookie consent values.
     * @fires event:cookie-consent-changed
     */
    save(): void {
        const consentValue = this.buildConsentValuesFromCheckboxes();
        consentValue.VERSION = this.consentVersion;
        const values = JSON.stringify(consentValue);
        this.createCookieConsent(values);

        this.destroyUnwantedCookies();
        this.destroyUnwantedStorage();
        this.close();
        this.dispatchCustomEvent('cookie-consent-changed', values);
    }

    /**
     * Builds string for cookies.
     * @returns {CookieConsentValue}
     */
    buildConsentValuesFromCheckboxes(): CookieConsentValue {
        const checkboxes = this.querySelectorAll<HTMLInputElement>(
            '.ui-cookie-consent__category-checkbox'
        );
        return [...checkboxes].reduce(
            (prev: CookieConsentValue, current: HTMLInputElement) => {
                prev[current.name as CookieConsentCategory] = current.checked
                    ? UICookieConsent.AllowedCategory
                    : UICookieConsent.DisallowedCategory;
                return prev;
            },
            {} as CookieConsentValue
        );
    }

    /**
     * Handles accept all action
     * @private
     */
    private handleAccept(): void {
        this.selectAllCategories();
        this.save();
    }

    /**
     * Handles save choice selected settings action.
     * @private
     */
    private handleSaveChoice(): void {
        this.save();
    }

    /**
     * Handles decline selected settings action.
     * @private
     */
    private handleDecline(): void {
        this.deselectAdditionalCategories();
        this.save();
    }

    /**
     * Selects all categories checkboxes.
     * @private
     */
    private selectAllCategories(): void {
        this.querySelectorAll<HTMLInputElement>(
            '.ui-cookie-consent__category-checkbox'
        ).forEach((checkbox) => {
            checkbox.checked = true;
        });
    }

    /**
     * Deselects all categories checkboxes.
     * @private
     */
    private deselectAdditionalCategories(): void {
        [].filter
            .call(
                this.querySelectorAll<HTMLInputElement>(
                    '.ui-cookie-consent__category-checkbox'
                ),
                (checkbox: HTMLInputElement) => {
                    return checkbox.name !== UICookieConsent.Category.Necessary;
                }
            )
            .forEach(
                (checkbox: HTMLInputElement) => (checkbox.checked = false)
            );
    }

    /**
     * Creates help tooltip for category.
     * @param {CookieConsentCategory} category
     * @returns {IElementConfig}
     * @private
     */
    private createCategoryTooltip(
        category: CookieConsentCategory
    ): IElementConfig {
        return {
            tagName: 'ui-tooltip',
            children: [UICookieConsent.labels['HELP_' + category]],
        };
    }

    /**
     * Checks if cookie is whitelisted and allowed.
     * @param {string} cookieName
     * @returns {boolean}
     */
    isCookieWhitelisted(cookieName: string): boolean {
        const keys = Object.keys(
            UICookieConsent.whitelist
        ) as CookieConsentCategory[];
        const found = keys.reduce<string[]>((acc, category) => {
            const categories = UICookieConsent.whitelist[category].filter(
                (categoryName) => cookieName.indexOf(categoryName) === 0
            );
            return acc.concat(categories);
        }, []);
        if (found.length === 0) {
            return false;
        }
        return cookieName.includes(found[0]);
    }

    /**
     * Gets all cookies for current domain
     * @returns {string[]}
     */
    getAllCookiesList(): string[] {
        return document.cookie.split(';').map((c) => c.trim().split('=')[0]);
    }

    /**
     * Prepare placeholders with links and appropriate labels.
     *
     * Note! {{days}} isn't used anymore, but keep it, might again change in the future.
     * @returns {Record<string, string>}
     * @private
     */
    private getTextSubstitutions(): Record<
        string,
        string | TranslationLabels.Label
    > {
        return {
            country: this.resolveCountryName(),
            days: String(this.expiresDays),
            cookiesLink: this.hrefCookies
                ? this.createCookieLink().outerHTML
                : this.createCookieLink().innerHTML,
            policyLink: this.createPolicyLink().outerHTML,
            entitiesLink: this.createEntitiesLink().outerHTML,
            usageLink: this.createUsageLink().outerHTML,
        };
    }

    /**
     * @inheritDoc
     */
    render(): void {
        if (!this.country) {
            console.warn(
                'The country attribute is mandatory for ui-cookie-consent'
            );
            console.warn('EE, LV or LT should be set to country');
        }

        const substitutions = this.getTextSubstitutions();

        this.insertElements(
            [
                {
                    tagName: 'ui-modal',
                    attributes: {
                        type: 'page',
                        compact: true,
                        closable: false,
                        tabindex: TabIndex.Active,
                        layout: UIModal.Layouts.Default,
                    },
                    classList: {
                        'ui-cookie-consent__container': true,
                    },
                    children: [
                        {
                            tagName: 'div',
                            classList: {
                                'ui-cookie-consent__inner': true,
                            },
                            children: [
                                this.createLanguageSelector(),
                                {
                                    tagName: 'div',
                                    classList: {
                                        'ui-cookie-consent__views': true,
                                    },
                                    children: [
                                        {
                                            tagName: 'h2',
                                            classList: {
                                                'ui-cookie-consent__title':
                                                    true,
                                            },
                                            children: [
                                                this.getLabel(
                                                    'title',
                                                    substitutions
                                                ),
                                            ],
                                        },
                                        {
                                            tagName: 'p',
                                            children: [
                                                this.getLabel(
                                                    'info',
                                                    substitutions
                                                ),
                                            ],
                                        },
                                        {
                                            tagName: 'div',
                                            classList: {
                                                'ui-cookie-consent__controls':
                                                    true,
                                            },
                                            children: [
                                                {
                                                    tagName: 'button',
                                                    attributes: {
                                                        type: 'button',
                                                    },
                                                    classList: {
                                                        button: true,
                                                        'ui-cookie-consent__accept-button':
                                                            true,
                                                    },
                                                    children: [
                                                        UICookieConsent.labels
                                                            .accept,
                                                    ],
                                                },
                                                {
                                                    tagName: 'button',
                                                    attributes: {
                                                        type: 'button',
                                                    },
                                                    classList: {
                                                        'ui-cookie-consent__decline-button':
                                                            true,
                                                        button: true,
                                                    },
                                                    children: [
                                                        UICookieConsent.labels
                                                            .decline,
                                                    ],
                                                },
                                            ],
                                        },
                                        {
                                            tagName: 'ui-curtains',
                                            children: [
                                                {
                                                    tagName: 'ui-curtain',
                                                    classList: {
                                                        'ui-cookie-consent__settings':
                                                            true,
                                                    },
                                                    attributes: {
                                                        label: UICookieConsent
                                                            .labels.myCookies,
                                                    },
                                                    children: [
                                                        {
                                                            tagName: 'ui-field',
                                                            attributes: {
                                                                layout: 'fluid',
                                                            },
                                                            children: [
                                                                this.buildCategoriesList(),
                                                            ],
                                                        },
                                                        {
                                                            tagName: 'div',
                                                            classList: {
                                                                'ui-cookie-consent__controls':
                                                                    true,
                                                            },
                                                            children: [
                                                                {
                                                                    tagName:
                                                                        'button',
                                                                    attributes:
                                                                        {
                                                                            type: 'button',
                                                                        },
                                                                    classList: {
                                                                        button: true,
                                                                        'ui-cookie-consent__save-choice-button':
                                                                            true,
                                                                    },
                                                                    children: [
                                                                        UICookieConsent
                                                                            .labels
                                                                            .saveChoice,
                                                                    ],
                                                                },
                                                            ],
                                                        },
                                                    ],
                                                },
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                        {
                            tagName: 'p',
                            classList: {
                                'ui-cookie-consent__footer': true,
                                disclaimer: true,
                            },
                            children: [
                                this.getLabel('disclaimer', substitutions),
                            ],
                        },
                    ],
                },
            ],
            'beforeend',
            true
        );

        if (this.forceShow) {
            this.open();
            return;
        }

        if (this.isConsentGiven() || this.forceHide) {
            if (this.container.isActive()) {
                this.close();
            }
        } else {
            this.open();
        }
    }

    /**
     * Create language selector for ui-cookie-consent.
     * @returns {IElementConfig}
     * @private
     */
    createLanguageSelector(): IElementConfig {
        const languages = getLanguageForCountry(this.country);
        const currentLang = this.language;
        return {
            tagName: 'ui-dropdown',
            classList: {
                'ui-cookie-consent__lang': true,
                '-hidden': !this.language,
            },
            attributes: {
                layout: 'lang',
                'mobile-native': true,
                'hide-selected': true,
            },
            children: [
                {
                    tagName: 'select',
                    attributes: {
                        'aria-label': UICookieConsent.labels.language,
                    },
                    children: languages.short.map((_, i) => {
                        return {
                            tagName: 'option',
                            attributes: {
                                value: languages.long[i],
                                selected: languages.long[i] === currentLang,
                            },
                            children: [languages.short[i]],
                        };
                    }),
                },
            ],
        };
    }

    /**
     * Handles language switch.
     * @param {CustomEvent} event
     * @throws {Error}
     */
    handleLanguageSwitch(event: CookieConsentLanguageChangeEvent): void {
        if (!event) {
            throw new Error('language change event is not set');
        }
        if (typeof this.languageSwitcher === 'function') {
            // If language stays empty, the service using this component, itself
            // will fall back to default language.
            let language: Language = '';
            if (
                event.target instanceof UIDropdown ||
                event.target instanceof HTMLSelectElement
            ) {
                language = event.target.value;
            }
            this.languageSwitcher(language);
        } else {
            console.warn(
                "UICookieConsent.languageSwitcher function isn't defined"
            );
        }
    }

    /**
     * @inheritDoc
     */
    hydrate(): void {
        updateElement(this.acceptButton, {
            events: {
                click: this.handleAccept.bind(this),
            },
        });
        updateElement(this.declineButton, {
            events: {
                click: this.handleDecline.bind(this),
            },
        });
        updateElement(this.saveChoiceButton, {
            events: {
                click: this.handleSaveChoice.bind(this),
            },
        });
        updateElement(this.langSelector, {
            events: {
                change: this.handleLanguageSwitch.bind(this),
            },
        });

        // Some cookies set on weird images on ibank, so clean-up again on window.load.
        this.windowLoadHandler = () => {
            this.destroyUnwantedCookies();
            this.destroyUnwantedStorage();
        };
        window.addEventListener('load', this.windowLoadHandler);
    }

    /**
     * @inheritDoc
     */
    disconnect(): void {
        window.removeEventListener('load', this.windowLoadHandler);
    }

    /**
     * @inheritDoc
     */
    reconnect(): void {
        window.addEventListener('load', this.windowLoadHandler);
    }
}
